import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Award,
  Baby,
  Building2,
  Calculator,
  HardHat,
  List,
  Map as MapIcon,
  MapPin,
  Printer,
  RefreshCw,
  Send,
  Shield,
  ShieldCheck,
  Smartphone,
  Sparkles,
  TrendingUp,
  Users,
  Utensils,
  type LucideIcon,
} from 'lucide-react';
import FloorPlanImage from '@components/FloorPlanImage';
import ReportSection from '@components/ReportSection';
import YieldPanel from '@components/YieldPanel';
import KhoudMap from '@components/KhoudMap';
import ShareModal from '@components/ShareModal';
import VirtualTour from '@components/VirtualTour';
import ProjectShowcase from '@components/ProjectShowcase';
import MapExperience from '@components/MapExperience';
import NewCustomerButton from '@components/NewCustomerButton';
import project from '@data/project.json';
import marketData from '@data/market.json';
import poisData from '@data/pois.json';
import amenitiesData from '@data/amenities.json';
import {
  calculatePrice,
  formatOMR,
  formatPercentValue,
  getMidScenario,
  type PriceBreakdown,
} from '@lib/pricing';
import { useT, type StringKey } from '@lib/i18n';
import { useSessionStore } from '@stores/sessionStore';
import { useCatalogStore } from '@stores/catalogStore';
import type {
  MarketListing,
  PaymentPlan,
  POI,
  Session,
  Unit,
} from '@lib/types';

type AppendixKey = 'market' | 'pois' | 'amenities' | 'fullTable';
type CollapseStates = Record<AppendixKey, boolean>;
const ALL_OPEN: CollapseStates = {
  market: true,
  pois: true,
  amenities: true,
  fullTable: true,
};
const ALL_CLOSED: CollapseStates = {
  market: false,
  pois: false,
  amenities: false,
  fullTable: false,
};

const TODAY_OPTS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
};

interface Amenity {
  id: string;
  name_ar: string;
  name_en: string;
  desc_ar: string;
  desc_en: string;
  category: string;
}

const AMENITY_ICONS: Record<string, LucideIcon> = {
  lifestyle: Sparkles,
  family: Baby,
  building: Building2,
  security: Shield,
  tech: Smartphone,
  interior: Utensils,
};

export default function Step6Report() {
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  // When rendered as part of /r/:sessionId (shared link), the parent has
  // already hydrated stores from the API — skip the "no unit → /match" guard.
  const isShared = location.pathname.startsWith('/r/');

  const language = useSessionStore((s) => s.language);
  const clientName = useSessionStore((s) => s.clientName);
  const clientPhone = useSessionStore((s) => s.clientPhone);
  const clientEmail = useSessionStore((s) => s.clientEmail);
  const advisorName = useSessionStore((s) => s.advisorName);
  // B.4-fix3 (2026-05-08) — advisor identity fields are required by the
  // Worker's invalid_advisor validation and consumed by the respond.io
  // template/assignee flow downstream of /api/sessions/share.
  const advisorId = useSessionStore((s) => s.advisorId);
  const advisorRespondId = useSessionStore((s) => s.advisorRespondId);
  const sessionId = useSessionStore((s) => s.sessionId);
  const startedAt = useSessionStore((s) => s.startedAt);
  const completedAt = useSessionStore((s) => s.completedAt);
  const selectedUnitCode = useSessionStore((s) => s.selectedUnit);
  const paymentPlan = useSessionStore((s) => s.paymentPlan);
  const goal = useSessionStore((s) => s.goal);
  const budget = useSessionStore((s) => s.budget);
  const bedrooms = useSessionStore((s) => s.bedrooms);
  const priority = useSessionStore((s) => s.priority);
  const decision = useSessionStore((s) => s.decision);
  const stepTimes = useSessionStore((s) => s.stepTimes);
  const completeSession = useSessionStore((s) => s.completeSession);
  const resetSession = useSessionStore((s) => s.resetSession);

  const filteredUnits = useCatalogStore((s) => s.filteredUnits);

  const isAr = language === 'ar';

  const unit = useMemo<Unit | null>(
    () =>
      filteredUnits.find((u) => u.unitCode === selectedUnitCode) ?? null,
    [filteredUnits, selectedUnitCode]
  );

  // Guard: if user lands on /report without a selected unit, send them back.
  // (Skipped on /r/:id — SharedReport handles its own loading/empty states.)
  useEffect(() => {
    if (!unit && !isShared) {
      navigate('/match', { replace: true });
    }
  }, [unit, isShared, navigate]);

  const breakdown = useMemo(
    () => (unit ? calculatePrice(unit.basePrice) : null),
    [unit]
  );

  // Phase 8f-bcs (2026-05-07) — yieldBreakdown now reads the operator-
  // curated mid scenario from the unit instead of deriving yield from a
  // rent assumption.
  const yieldBreakdown = useMemo(
    () => (unit ? getMidScenario(unit) : null),
    [unit]
  );

  if (!unit || !breakdown || !yieldBreakdown) return null;

  const todayStr = new Date().toLocaleDateString(isAr ? 'ar-OM' : 'en-GB', TODAY_OPTS);
  const planUsed: PaymentPlan = paymentPlan ?? 'monthly';

  // ─── Snapshot of session passed to PDF + WhatsApp builders ─────
  const sessionSnapshot = useMemo<Session>(
    () => ({
      sessionId,
      startedAt,
      completedAt,
      advisorName,
      advisorId,
      advisorRespondId,
      clientName,
      clientPhone,
      clientEmail,
      language,
      goal,
      budget,
      bedrooms,
      priority,
      selectedUnit: selectedUnitCode,
      paymentPlan,
      decision,
      stepTimes,
    }),
    [
      sessionId, startedAt, completedAt, advisorName, advisorId, advisorRespondId,
      clientName, clientPhone, clientEmail,
      language, goal, budget, bedrooms, priority, selectedUnitCode, paymentPlan, decision, stepTimes,
    ]
  );

  // ─── Collapse state for the 4 appendices (lifted so handlePrint can force-open). ─
  // Phase 8f-bcs B.2 fix (2026-05-08) — server-side PDF generation visits
  // /r/{id}?print=1 in headless Chrome, so we must seed the collapse
  // state expanded BEFORE the snapshot. The Worker-driven puppeteer flow
  // never invokes handlePrint() (that's a UI-only handler), so without
  // this seed the appendices print collapsed.
  const isPrintMode = useMemo(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('print') === '1';
    } catch { return false; }
  }, []);
  const [collapseStates, setCollapseStates] = useState<CollapseStates>(
    isPrintMode ? ALL_OPEN : ALL_CLOSED,
  );
  const setSection = (key: AppendixKey, open: boolean) =>
    setCollapseStates((s) => ({ ...s, [key]: open }));

  // ─── Share modal visibility ────────────────────────────────────
  const [showShareModal, setShowShareModal] = useState(false);
  // ─── V3 Day 1 — VR modal visibility ────────────────────────────
  const [showVr, setShowVr] = useState(false);
  // V3 Day 2 — Cinematic project showcase visibility (local).
  const [showShowcase, setShowShowcase] = useState(false);
  // V3 Day 3.1 — Interactive map experience modal (local).
  const [showMap, setShowMap] = useState(false);

  // ─── Action handlers ───────────────────────────────────────────
  const handlePrint = useCallback(() => {
    // Force-expand every appendix so it lands in the printed PDF.
    setCollapseStates(ALL_OPEN);
    // Wait one frame for re-render, then trigger native print.
    requestAnimationFrame(() => {
      window.setTimeout(() => window.print(), 100);
    });
  }, []);

  const handleCrm = () => window.alert(t('report.placeholderCrm'));
  const handleReserve = () => {
    completeSession('reserved');
    window.alert(t('report.placeholderReserved'));
  };
  const handleNewSession = () => {
    resetSession();
    navigate('/welcome');
  };

  // ─── Section 1: Project ─────────────────────────────────────────
  const stats = [
    { value: project.totalUnits, label: t('project.units') },
    { value: project.floors, label: t('project.floors') },
    { value: project.shops, label: t('project.shops') },
    { value: project.amenitiesCount, label: t('match.areaUnit') === 'م²' ? 'مرفق' : 'amenities' },
  ];

  // ─── Section 5: Market ─────────────────────────────────────────
  const marketComparables = useMemo(() => {
    const all = marketData as MarketListing[];
    const matched = all.filter((m) => m.beds === unit.beds);
    matched.sort((a, b) => Math.abs(a.area - unit.area) - Math.abs(b.area - unit.area));
    return matched.slice(0, 10);
  }, [unit.beds, unit.area]);

  const marketAvg = useMemo(() => {
    const set = (marketData as MarketListing[]).filter((m) => m.beds === unit.beds);
    if (set.length === 0) return null;
    return set.reduce((s, m) => s + m.price, 0) / set.length;
  }, [unit.beds]);

  // ─── Section 6: POIs ───────────────────────────────────────────
  const center: [number, number] = poisData.center as [number, number];
  const allPois = poisData.pois as POI[];
  const anchorPois = allPois.filter((p) => p.anchor);

  // ─── Section 7: Amenities ──────────────────────────────────────
  const amenities = (Array.isArray(amenitiesData)
    ? amenitiesData
    : (amenitiesData as { amenities: Amenity[] }).amenities) as Amenity[];

  // Phase 8f-bcs (2026-05-07) — Section 4 now reads unit.note verbatim
  // (operator-curated bullet narrative). The dynamic threshold-based
  // bullets that the v1 report used have been removed.

  const goalText = goal
    ? (isAr
        ? { investment: 'استثمار', living: 'سكن شخصي', both: 'سكن واستثمار معاً' }[goal]
        : { investment: 'investment', living: 'personal living', both: 'living + investment' }[goal])
    : '';

  return (
    <div className="step-container max-w-5xl">
      {/* V3 Day 3.0 — reset row above the cover (hidden in print). */}
      <div className="flex justify-end mb-3 no-print">
        <NewCustomerButton />
      </div>

      {/* ── Cover ───────────────────────────────────────────────── */}
      <header className="rounded-2xl bg-gradient-to-br from-navy-900 via-navy-800 to-navy-700 text-white p-6 md:p-8 mb-4 shadow-md">
        <div className="flex items-center justify-between mb-4">
          <span className="inline-flex items-center gap-2 text-xs text-gold-300/80 uppercase tracking-wider">
            <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse-slow" />
            KEY2 BCS
          </span>
          <span className="text-xs text-white/50 num-en">{todayStr}</span>
        </div>
        <h1 className="text-3xl md:text-4xl font-bold mb-2 leading-tight">
          {t('report.title')}
        </h1>
        <p className="text-base md:text-lg text-white/80">
          <span className="num-en font-semibold text-gold">{unit.unitCode}</span>
          <Sep />
          {unit.type}
          <Sep />
          <span className="num-en">{unit.area}</span> {t('match.areaUnit')}
          <Sep />
          <span className="text-white/70">{unit.floor}</span>
        </p>
        <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <MetaCell label={t('report.client')} value={clientName || '—'} />
          <MetaCell label={t('report.advisor')} value={advisorName || '—'} />
          <MetaCell label={t('report.date')} value={todayStr} mono />
          <MetaCell
            label={t('report.sessionId')}
            value={sessionId.slice(0, 8)}
            mono
          />
        </div>
      </header>

      {/* ── Unit floor plan (Phase 8f-bcs B.2 fix, 2026-05-08) ──── */}
      <section className="mb-6">
        <h2 className="text-sm font-semibold text-navy-600 mb-2 text-center">
          {t('match.floorPlanAlt')} —{' '}
          <span className="num-en font-bold text-navy">{unit.unitCode}</span>
        </h2>
        <div className="max-w-[600px] mx-auto rounded-xl overflow-hidden border border-navy-100 shadow-sm">
          <FloorPlanImage unit={unit} size="large" />
        </div>
      </section>

      {/* ── Sticky action bar (hidden in print) ─────────────────── */}
      <div className="sticky top-0 z-10 -mx-4 md:mx-0 mb-6 bg-white/85 backdrop-blur border-b border-navy-100 py-2 md:py-3 px-4 md:px-2 sticky-action-bar no-print">
        <div className="flex flex-wrap gap-2 justify-center md:justify-end">
          <ActionBtn
            icon={Send}
            onClick={() => setShowShareModal(true)}
            label={t('report.shareToClient')}
            title={t('report.shareTooltip')}
          />
          <ActionBtn
            icon={Printer}
            onClick={handlePrint}
            label={t('report.printSelf')}
            title={t('report.printSelfTooltip')}
            ghost
          />
          {!isShared && (
            <>
              <ActionBtn icon={Users} onClick={handleCrm} label={t('report.saveToCrm')} />
              <ActionBtn icon={Award} onClick={handleReserve} label={t('report.reserve')} primary />
              <ActionBtn icon={RefreshCw} onClick={handleNewSession} label={t('report.newSession')} ghost />
            </>
          )}
        </div>
      </div>

      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        session={sessionSnapshot}
        unit={unit}
        breakdown={breakdown}
        yieldData={yieldBreakdown}
        language={language}
      />

      {showVr && (
        <VirtualTour unitType={unit.type} onClose={() => setShowVr(false)} />
      )}
      <ProjectShowcase
        open={showShowcase}
        onClose={() => setShowShowcase(false)}
      />
      <MapExperience open={showMap} onClose={() => setShowMap(false)} />

      <div className="space-y-4">
        {/* ── Section 1: Project ─────────────────────────────────── */}
        <ReportSection
          icon={<MapPin className="w-6 h-6" />}
          title={t('report.s1Title')}
          subtitle={t('report.s1Subtitle')}
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {stats.map((s, i) => (
              <div key={i} className="stat-card">
                <div className="stat-value">
                  <span className="num-en">{s.value}</span>
                </div>
                <div className="stat-label">{s.label}</div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {project.demandClusters.map((c) => (
              <div key={c.name_en} className="p-3 rounded-lg bg-navy-50/60 border border-navy-100">
                <div className="flex items-baseline justify-between mb-1">
                  <h4 className="font-bold text-navy text-sm">
                    {isAr ? c.name_ar : c.name_en}
                  </h4>
                  <span className="text-xs text-navy-500 num-en">
                    {c.anchor_count}
                  </span>
                </div>
                <p className="text-xs text-navy-600 leading-relaxed">
                  {isAr ? c.desc_ar : c.desc_en}
                </p>
              </div>
            ))}
          </div>
        </ReportSection>

        {/* ── Section 2: Numbers ─────────────────────────────────── */}
        <ReportSection
          icon={<Calculator className="w-6 h-6" />}
          title={t('report.s2Title')}
          subtitle={t('report.s2Subtitle')}
        >
          {/* V3 Day 1 — VR entry / V3 Day 2 — Showcase entry (no-print). */}
          <div className="mb-3 flex justify-end gap-2 no-print">
            <button
              type="button"
              onClick={() => setShowShowcase(true)}
              className="!py-1.5 !px-3 text-xs md:text-sm rounded-full font-bold text-navy
                         bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-400 hover:to-yellow-500
                         shadow-md transition-all"
              title={t('showcase.title')}
            >
              {t('showcase.discoverKey2')}
            </button>
            <button
              type="button"
              onClick={() => setShowVr(true)}
              className="btn-gold !py-1.5 !px-3 text-xs md:text-sm"
              title={t('vr.title')}
            >
              {t('vr.button')}
            </button>
          </div>
          <div className="grid md:grid-cols-1 gap-4">
            <PriceTable breakdown={breakdown} plan={planUsed} t={t} />
            <YieldPanel unit={unit} highlight="mid" />
          </div>
        </ReportSection>

        {/* ── Section 3: Guarantees ──────────────────────────────── */}
        <ReportSection
          icon={<ShieldCheck className="w-6 h-6" />}
          title={t('report.s3Title')}
          subtitle={t('report.s3Subtitle')}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {project.guarantees.slice(0, 4).map((g) => {
              const Icon = guaranteeIcon(g.id);
              return (
                <div
                  key={g.id}
                  className="flex gap-3 p-3 rounded-lg border border-navy-100 bg-white"
                >
                  <div className="w-10 h-10 rounded-full bg-gold-50 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-navy-700" strokeWidth={1.75} />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-navy mb-0.5">
                      {isAr ? g.title_ar : g.title_en}
                    </h4>
                    <p className="text-xs text-navy-600 leading-relaxed">
                      {isAr ? g.desc_ar : g.desc_en}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="mt-3 text-xs text-navy-500 leading-relaxed">
            {t('guar.delayProtectionNote')}
          </p>
        </ReportSection>

        {/* ── Section 4: Recommendation ──────────────────────────── */}
        <ReportSection
          icon={<Award className="w-7 h-7" />}
          title={t('report.s4Title')}
          subtitle={t('report.s4Subtitle')}
          accent="gold"
        >
          {goal && (
            <p className="text-sm text-navy-700 mb-3 leading-relaxed">
              {fillTemplate(t('report.recoGoalLine'), { goal: goalText })}
            </p>
          )}
          <p className="text-sm text-navy-700 leading-relaxed whitespace-pre-line">
            {unit.note}
          </p>
        </ReportSection>

        {/* ── Section 4b/4c: Investor + Resident summaries ───────── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <SummaryCard
            tone="gold"
            icon="💼"
            title={t('report.investorSummary')}
            text={unit.investorScript}
          />
          <SummaryCard
            tone="navy"
            icon="🏠"
            title={t('report.residentSummary')}
            text={unit.residentScript}
          />
        </div>

        {/* ── Section 5: Market (appendix, page-break before in print) ─ */}
        <div className="pdf-page-break">
          <ReportSection
            icon={<TrendingUp className="w-6 h-6" />}
            title={t('report.s5Title')}
            subtitle={t('report.s5Subtitle')}
            appendixLabel={t('report.appendix')}
            collapsible
            open={collapseStates.market}
            onOpenChange={(o) => setSection('market', o)}
          >
            {marketComparables.length === 0 ? (
              <p className="text-sm text-navy-500">{t('report.marketEmpty')}</p>
            ) : (
              <>
                {marketAvg !== null && <MarketAvgLine avg={marketAvg} unitPrice={unit.basePrice} t={t} />}
                <MarketTable rows={marketComparables} t={t} />
              </>
            )}
          </ReportSection>
        </div>

        {/* ── Section 6: POIs (appendix) ────────────────────────── */}
        <ReportSection
          icon={<MapIcon className="w-6 h-6" />}
          title={t('report.s6Title')}
          subtitle={t('report.s6Subtitle')}
          appendixLabel={t('report.appendix')}
          collapsible
          open={collapseStates.pois}
          onOpenChange={(o) => setSection('pois', o)}
        >
          <KhoudMap
            center={center}
            pois={anchorPois}
            ariaLabel={t('project.mapAriaLabel')}
            className="h-[260px] md:h-[320px] w-full rounded-xl overflow-hidden border border-navy-100 mb-3"
          />
          {/* V3 Day 3.1 — entry to the full interactive map (no-print). */}
          <div className="mb-3 flex justify-end no-print">
            <button
              type="button"
              onClick={() => setShowMap(true)}
              className="!py-1.5 !px-3 text-xs md:text-sm rounded-full font-semibold border border-navy-200 text-navy hover:bg-navy hover:text-gold transition-colors"
              title={t('mapExperience.title')}
            >
              {t('mapExperience.openFull')}
            </button>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            {anchorPois
              .filter((p) => p.category !== 'project')
              .map((p) => (
                <li
                  key={p.name_en}
                  className="flex items-baseline justify-between p-2 rounded border border-navy-100 bg-white"
                >
                  <span className="font-medium text-navy">
                    {isAr ? p.name_ar : p.name_en}
                  </span>
                  <span className="text-xs text-navy-500 num-en flex-shrink-0">
                    {typeof p.distance_min === 'number'
                      ? `${p.distance_min} ${t('common.minute')}`
                      : ''}
                  </span>
                </li>
              ))}
          </ul>
        </ReportSection>

        {/* ── Section 7: Amenities (appendix) ───────────────────── */}
        <ReportSection
          icon={<Building2 className="w-6 h-6" />}
          title={t('report.s7Title')}
          subtitle={t('report.s7Subtitle')}
          appendixLabel={t('report.appendix')}
          collapsible
          open={collapseStates.amenities}
          onOpenChange={(o) => setSection('amenities', o)}
        >
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {amenities.map((a) => {
              const Icon = AMENITY_ICONS[a.category] ?? Building2;
              return (
                <div
                  key={a.id}
                  className="p-3 rounded-lg border border-navy-100 bg-white"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className="w-4 h-4 text-gold-700" strokeWidth={1.75} />
                    <h4 className="text-sm font-semibold text-navy">
                      {isAr ? a.name_ar : a.name_en}
                    </h4>
                  </div>
                  <p className="text-xs text-navy-500 leading-relaxed">
                    {isAr ? a.desc_ar : a.desc_en}
                  </p>
                </div>
              );
            })}
          </div>
        </ReportSection>

        {/* ── Section 8: Full table (appendix, page-break before in print) ─ */}
        <div className="pdf-page-break pdf-allow-break">
          <ReportSection
            icon={<List className="w-6 h-6" />}
            title={t('report.s8Title')}
            subtitle={t('report.s8Subtitle')}
            appendixLabel={t('report.appendix')}
            collapsible
            open={collapseStates.fullTable}
            onOpenChange={(o) => setSection('fullTable', o)}
          >
            <FullTable
              units={filteredUnits}
              selectedCode={unit.unitCode}
              t={t}
            />
          </ReportSection>
        </div>
      </div>

      {/* ── Footer ─────────────────────────────────────────────── */}
      <footer className="mt-8 pt-6 border-t border-navy-100 text-xs text-navy-400">
        <p className="leading-relaxed mb-2">{t('report.disclaimer')}</p>
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <span>{t('report.brand')} · KEY2 BCS</span>
          <span className="num-en">
            {sessionId.slice(0, 8)} · {todayStr}
          </span>
        </div>
      </footer>
    </div>
  );
}

/* ─── helpers ─── */

function fillTemplate(s: string, vars: Record<string, string | number>): string {
  return s.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? ''));
}

function guaranteeIcon(id: string): LucideIcon {
  switch (id) {
    case 'escrow':
      return ShieldCheck;
    case 'construction':
      return HardHat;
    case 'reports':
      return Calculator; // doc-ish icon; FileText already used in Step 5
    case 'owners_assoc':
      return Users;
    default:
      return ShieldCheck;
  }
}

function Sep() {
  return <span className="mx-2 text-white/30">·</span>;
}

function MetaCell({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-lg px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-gold-300/70 mb-0.5">
        {label}
      </div>
      <div className={`font-semibold text-sm truncate ${mono ? 'num-en' : ''}`}>
        {value}
      </div>
    </div>
  );
}

function ActionBtn({
  icon: Icon,
  label,
  onClick,
  primary,
  ghost,
  title,
}: {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
  primary?: boolean;
  ghost?: boolean;
  title?: string;
}) {
  const cls = primary
    ? 'btn-primary'
    : ghost
      ? 'btn-ghost'
      : 'btn-gold';
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`${cls} !py-2 !px-3 text-xs md:text-sm`}
    >
      <Icon className="w-4 h-4" strokeWidth={1.75} />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

function PriceTable({
  breakdown,
  plan,
  t,
}: {
  breakdown: PriceBreakdown;
  plan: PaymentPlan;
  t: (k: StringKey) => string;
}) {
  const omr = t('common.omr');
  const rows: Array<[string, number, number]> = [
    [t('report.basePrice'), breakdown.basePrice, 0],
    [t('report.vat'), breakdown.vat, 0],
    [t('report.priceWithVat'), breakdown.priceWithVat, 0],
  ];

  return (
    <div className="border border-navy-100 rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <tbody>
          {rows.map(([label, val, dp]) => (
            <tr key={label} className="border-b border-navy-100 last:border-b-0">
              <td className="px-3 py-2 text-navy-600">{label}</td>
              <td className="px-3 py-2 text-end font-semibold text-navy">
                <span className="num-en">{formatOMR(val, { withSymbol: false, decimals: dp })}</span>
                <span className="text-xs text-navy-400 ms-1">{omr}</span>
              </td>
            </tr>
          ))}
          <tr className="bg-gold-50">
            <td className="px-3 py-2 text-navy-700 font-semibold" colSpan={2}>
              {t('report.planLabel')}: {planLabel(plan, t)}
            </td>
          </tr>
          {planRows(breakdown, plan, t).map(([label, val, dp]) => (
            <tr key={label} className="border-b border-navy-100 last:border-b-0 bg-gold-50/40">
              <td className="px-3 py-2 text-navy-600">{label}</td>
              <td className="px-3 py-2 text-end font-semibold text-navy">
                <span className="num-en">{formatOMR(val, { withSymbol: false, decimals: dp })}</span>
                <span className="text-xs text-navy-400 ms-1">{omr}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function planLabel(plan: PaymentPlan, t: (k: StringKey) => string): string {
  switch (plan) {
    case 'cash': return t('match.cash');
    case 'monthly': return t('match.monthly');
    case 'quarterly': return t('match.quarterly');
    case 'flex': return t('match.flex');
  }
}

function planRows(
  b: PriceBreakdown,
  plan: PaymentPlan,
  t: (k: StringKey) => string
): Array<[string, number, number]> {
  switch (plan) {
    case 'cash':
      return [[t('report.cashFinal'), b.cashFinal, 0]];
    case 'monthly':
      return [
        [t('match.downPayment'), b.downPayment, 0],
        [t('match.monthlyShort'), b.monthlyInst, 2],
      ];
    case 'quarterly':
      return [
        [t('match.downPayment'), b.downPayment, 0],
        [t('match.quarterlyShort'), b.quarterlyInst, 2],
      ];
    case 'flex':
      return [
        [t('match.downPayment'), b.downPayment, 0],
        [t('match.flexFirstMonths'), b.flex.initialMonthly, 0],
        [t('match.flexAfterMonths'), b.flex.midMonthly, 2],
        [t('match.flexScheduleM40'), b.flex.settlementMonth, 2],
      ];
  }
}

/**
 * Phase 8f-bcs — operator-curated investor / resident summary card.
 * Two of these sit side-by-side after Section 4 of the report. Tone
 * controls the colour: 'gold' for investor, 'navy' for resident.
 */
function SummaryCard({
  tone,
  icon,
  title,
  text,
}: {
  tone: 'gold' | 'navy';
  icon: string;
  title: string;
  text: string;
}) {
  const cls =
    tone === 'gold'
      ? 'border-gold/40 bg-gold-50'
      : 'border-navy-300 bg-navy-50';
  const titleCls = tone === 'gold' ? 'text-gold-700' : 'text-navy';
  return (
    <div className={`rounded-xl border p-4 md:p-5 ${cls}`}>
      <h3 className={`text-base md:text-lg font-bold mb-2 ${titleCls}`}>
        <span className="me-1">{icon}</span>
        {title}
      </h3>
      <p className="text-sm text-navy-700 leading-relaxed whitespace-pre-line">
        {text}
      </p>
    </div>
  );
}

function MarketAvgLine({
  avg,
  unitPrice,
  t,
}: {
  avg: number;
  unitPrice: number;
  t: (k: StringKey) => string;
}) {
  const cheaper = unitPrice < avg;
  const diff = Math.abs(((unitPrice - avg) / avg) * 100);
  const key: StringKey = cheaper ? 'report.marketAvgLine' : 'report.marketAvgLineHigher';
  const text = fillTemplate(t(key), {
    avg: formatOMR(avg, { withSymbol: false }),
    pct: diff.toFixed(1),
  });
  return (
    <p
      className={`p-3 rounded-lg text-sm mb-3 ${cheaper ? 'bg-gold-50 border border-gold/30 text-navy' : 'bg-navy-50 border border-navy-100 text-navy-700'}`}
    >
      {text}
    </p>
  );
}

function MarketTable({
  rows,
  t,
}: {
  rows: MarketListing[];
  t: (k: StringKey) => string;
}) {
  return (
    <div className="overflow-x-auto -mx-4 md:mx-0">
      <table className="w-full text-xs md:text-sm min-w-[640px]">
        <thead className="bg-navy-50 text-navy-600">
          <tr>
            <Th>{t('report.marketSource')}</Th>
            <Th align="end">{t('report.marketBeds')}</Th>
            <Th align="end">{t('report.marketArea')}</Th>
            <Th align="end">{t('report.marketPrice')}</Th>
            <Th align="end">{t('report.marketPpsqm')}</Th>
            <Th>{t('report.marketDate')}</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr
              key={`${r.title}-${i}`}
              className="border-b border-navy-100 last:border-b-0"
            >
              <Td>{r.source}</Td>
              <Td align="end" mono>{r.beds}</Td>
              <Td align="end" mono>{r.area}</Td>
              <Td align="end" mono>{formatOMR(r.price, { withSymbol: false })}</Td>
              <Td align="end" mono>{formatOMR(Math.round(r.price / r.area), { withSymbol: false })}</Td>
              <Td>{r.date}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FullTable({
  units,
  selectedCode,
  t,
}: {
  units: Unit[];
  selectedCode: string;
  t: (k: StringKey) => string;
}) {
  return (
    <div className="overflow-x-auto -mx-4 md:mx-0">
      <table className="w-full text-xs md:text-sm min-w-[760px]">
        <thead className="bg-navy-50 text-navy-600">
          <tr>
            <Th>{t('report.tableUnit')}</Th>
            <Th>{t('report.tableType')}</Th>
            <Th>{t('report.tableFloor')}</Th>
            <Th align="end">{t('report.tableArea')}</Th>
            <Th>{t('report.tableView')}</Th>
            <Th align="end">{t('report.tableBase')}</Th>
            <Th align="end">{t('report.tableTotal')}</Th>
            <Th align="end">{t('report.tableMonthly')}</Th>
            <Th align="end">{t('report.tableNetYield')}</Th>
          </tr>
        </thead>
        <tbody>
          {units.map((u) => {
            const b = calculatePrice(u.basePrice);
            const mid = getMidScenario(u);
            const highlighted = u.unitCode === selectedCode;
            return (
              <tr
                key={u.unitCode}
                className={`border-b border-navy-100 last:border-b-0 ${highlighted ? 'bg-gold-50 font-semibold' : ''}`}
              >
                <Td mono>{u.unitCode}</Td>
                <Td>{u.type}</Td>
                <Td>{u.floor}</Td>
                <Td align="end" mono>{u.area}</Td>
                <Td>{u.floor}</Td>
                <Td align="end" mono>{formatOMR(u.basePrice, { withSymbol: false })}</Td>
                <Td align="end" mono>{formatOMR(b.priceWithVat, { withSymbol: false })}</Td>
                <Td align="end" mono>{formatOMR(b.monthlyInst, { withSymbol: false, decimals: 2 })}</Td>
                <Td align="end" mono>{formatPercentValue(mid.yieldPct)}</Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Th({
  children,
  align = 'start',
}: {
  children: React.ReactNode;
  align?: 'start' | 'end';
}) {
  return (
    <th
      className={`px-3 py-2 text-${align} text-xs font-semibold tracking-wide`}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align = 'start',
  mono,
}: {
  children: React.ReactNode;
  align?: 'start' | 'end';
  mono?: boolean;
}) {
  return (
    <td className={`px-3 py-2 text-${align} text-navy-700 ${mono ? 'num-en' : ''}`}>
      {children}
    </td>
  );
}
